import React, { useState, useRef, useEffect } from 'react';
import { Camera, Upload, X, Check, AlertCircle } from 'lucide-react';

const PhotoUpload = ({ onPhotosChange, existingPhotos = [] }) => {
  const [photos, setPhotos] = useState(existingPhotos);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  const MAX_PHOTOS = 6;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  
  // Log component mount
  console.log('📸 PhotoUpload component mounted');

  useEffect(() => {
    loadExistingPhotos();
  }, []);

  const loadExistingPhotos = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/photos/my-photos`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const loadedPhotos = data.photos.map(photo => ({
          id: photo.id,
          url: `${process.env.REACT_APP_BACKEND_URL}${photo.url}`,
          verified: photo.verified,
          isPrimary: photo.is_primary
        }));
        setPhotos(loadedPhotos);
        onPhotosChange?.(loadedPhotos);
      }
    } catch (error) {
      console.error('Error loading photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateFile = (file) => {
    const errors = [];
    
    if (!ALLOWED_TYPES.includes(file.type)) {
      errors.push('Please upload a JPEG, PNG, or WebP image');
    }
    
    if (file.size > MAX_FILE_SIZE) {
      errors.push('Image must be less than 5MB');
    }
    
    return errors;
  };

  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const uploadPhoto = async (file) => {
    const photoId = Date.now() + Math.random();
    console.log('🎯 uploadPhoto called for:', file.name);
    
    try {
      // Validate file
      console.log('🔍 Validating file...');
      const validationErrors = validateFile(file);
      if (validationErrors.length > 0) {
        console.error('❌ Validation failed:', validationErrors);
        throw new Error(validationErrors[0]);
      }
      console.log('✅ File validated');
      
      // Compress image
      console.log('🗜️ Compressing image...');
      setUploadProgress(prev => ({ ...prev, [photoId]: 20 }));
      const compressedFile = await compressImage(file);
      console.log('✅ Image compressed:', compressedFile.size, 'bytes');
      
      // Create form data
      const formData = new FormData();
      formData.append('file', compressedFile, file.name);
      
      setUploadProgress(prev => ({ ...prev, [photoId]: 40 }));
      
      // Upload to server
      const token = localStorage.getItem('token');
      const uploadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/upload-photo`;
      console.log('📡 Uploading to:', uploadUrl);
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      setUploadProgress(prev => ({ ...prev, [photoId]: 80 }));
      
      console.log('📨 Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Upload failed. Status:', response.status, 'Error:', errorText);
        throw new Error('Upload failed');
      }
      
      const data = await response.json();
      console.log('✅ Upload successful:', data);
      
      setUploadProgress(prev => ({ ...prev, [photoId]: 100 }));
      
      // Add photo to state
      const newPhoto = {
        id: data.photo_id || photoId,
        url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` || URL.createObjectURL(compressedFile),
        verified: data.verified || false,
        isPrimary: photos.length === 0 // First photo is primary
      };
      
      const updatedPhotos = [...photos, newPhoto];
      setPhotos(updatedPhotos);
      onPhotosChange?.(updatedPhotos);
      
      // Clean up progress
      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[photoId];
          return newProgress;
        });
      }, 2000);
      
    } catch (error) {
      console.error('❌ Photo upload error:', error);
      console.error('Error details:', error.message);
      alert(`Photo upload failed: ${error.message}`);
      
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[photoId];
        return newProgress;
      });
    }
  };

  const handleFileSelect = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    console.log('📁 File select triggered');
    const files = Array.from(event.target.files);
    console.log('📁 Files selected:', files.length);
    
    if (files.length === 0) {
      console.log('⚠️ No files selected');
      return;
    }
    
    if (photos.length + files.length > MAX_PHOTOS) {
      alert(`You can only upload up to ${MAX_PHOTOS} photos`);
      return;
    }
    
    setUploading(true);
    console.log('⏳ Starting upload process...');
    
    for (const file of files) {
      console.log('📤 Uploading file:', file.name, file.size, 'bytes');
      await uploadPhoto(file);
    }
    
    setUploading(false);
    console.log('✅ Upload process complete');
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = async (photoId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/photos/${photoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const updatedPhotos = photos.filter(photo => photo.id !== photoId);
      setPhotos(updatedPhotos);
      onPhotosChange?.(updatedPhotos);
      
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete photo');
    }
  };

  const setPrimaryPhoto = async (photoId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/photos/${photoId}/primary`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const updatedPhotos = photos.map(photo => ({
        ...photo,
        isPrimary: photo.id === photoId
      }));
      
      setPhotos(updatedPhotos);
      onPhotosChange?.(updatedPhotos);
      
    } catch (error) {
      console.error('Primary photo error:', error);
      alert('Failed to set primary photo');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Your Photos</h3>
        <p className="text-gray-600 text-sm">
          Add up to {MAX_PHOTOS} photos to showcase yourself (First photo will be your main photo)
        </p>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500"></div>
          <span className="ml-2 text-gray-600">Loading your photos...</span>
        </div>
      ) : (
        <>
          {/* Photo Grid */}
          <div className="grid grid-cols-3 gap-4">
        {/* Existing Photos */}
        {photos.map((photo) => (
          <div key={photo.id} className="relative aspect-square">
            <img
              src={photo.url}
              alt="Profile"
              className="w-full h-full object-cover rounded-2xl"
            />
            
            {/* Primary Badge */}
            {photo.isPrimary && (
              <div className="absolute top-2 left-2 bg-rose-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                Main
              </div>
            )}
            
            {/* Verification Badge */}
            {photo.verified && (
              <div className="absolute top-2 right-2 bg-green-500 rounded-full p-1">
                <Check className="h-3 w-3 text-white" />
              </div>
            )}
            
            {/* Actions */}
            <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity rounded-2xl flex items-center justify-center space-x-2">
              {!photo.isPrimary && (
                <button
                  type="button"
                  onClick={() => setPrimaryPhoto(photo.id)}
                  className="bg-white/20 backdrop-blur-sm text-white p-2 rounded-full hover:bg-white/30 transition-colors"
                  title="Set as main photo"
                >
                  <Camera className="h-4 w-4" />
                </button>
              )}
              
              <button
                type="button"
                onClick={() => removePhoto(photo.id)}
                className="bg-red-500/80 backdrop-blur-sm text-white p-2 rounded-full hover:bg-red-600/80 transition-colors"
                title="Remove photo"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        
        {/* Upload Progress Slots */}
        {Object.entries(uploadProgress).map(([photoId, progress]) => (
          <div key={photoId} className="aspect-square bg-gray-100 rounded-2xl flex items-center justify-center relative overflow-hidden">
            <div className="text-center">
              <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <div className="text-sm text-gray-600">{progress}%</div>
            </div>
            
            {/* Progress Bar */}
            <div className="absolute bottom-0 left-0 right-0 h-2 bg-gray-200">
              <div 
                className="h-full bg-rose-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        ))}
        
        {/* Upload Slots */}
        {Array.from({ length: MAX_PHOTOS - photos.length - Object.keys(uploadProgress).length }).map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('🖱️ Upload button clicked');
              console.log('📂 File input ref:', fileInputRef.current);
              fileInputRef.current?.click();
            }}
            disabled={uploading}
            className="aspect-square border-2 border-dashed border-gray-300 rounded-2xl flex flex-col items-center justify-center hover:border-rose-400 hover:bg-rose-50 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Camera className="h-8 w-8 text-gray-400 group-hover:text-rose-500 mb-2" />
            <span className="text-xs text-gray-500 group-hover:text-rose-600">Add Photo</span>
          </button>
        ))}
      </div>
          
          {/* File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_TYPES.join(',')}
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {/* Guidelines */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-blue-800 mb-1">Photo Guidelines:</p>
                <ul className="text-blue-700 space-y-1">
                  <li>• Use clear, recent photos of yourself</li>
                  <li>• Include at least one face photo</li>
                  <li>• Show your personality and interests</li>
                  <li>• Photos are reviewed for authenticity</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PhotoUpload;